import React, { useState, useRef } from 'react';
import {
  TextField,
  Label,
  Input,
} from 'react-aria-components';
import clsx from 'clsx';
import styles from './CustomTextInput.module.css';

export interface CustomTextInputProps {
  /**
   * Input label
   */
  label?: string;
  /**
   * Error message to display
   */
  error?: string;
  /**
   * Helper text to display below the input
   */
  helperText?: string;
  /**
   * Input type
   */
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search';
  /**
   * Full width input
   */
  fullWidth?: boolean;
  /**
   * Input value
   */
  value?: string;
  /**
   * Change handler
   */
  onChange?: (value: string) => void;
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Additional CSS class
   */
  className?: string;
  /**
   * Disabled state
   */
  disabled?: boolean;
  /**
   * Required field
   */
  required?: boolean;
  /**
   * Enable character counter
   */
  showCharCount?: boolean;
  /**
   * Maximum character length
   */
  maxLength?: number;
  /**
   * Enable clear button
   */
  clearable?: boolean;
  /**
   * Prefix icon or text
   */
  prefix?: React.ReactNode;
  /**
   * Suffix icon or text
   */
  suffix?: React.ReactNode;
  /**
   * Callback when input is cleared
   */
  onClear?: () => void;
  /**
   * Callback on blur
   */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * Callback on focus
   */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * Callback on key down
   */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /**
   * Name attribute for the input
   */
  name?: string;
  /**
   * ID attribute for the input
   */
  id?: string;
  /**
   * Auto complete attribute
   */
  autoComplete?: string;
  /**
   * Auto focus on mount
   */
  autoFocus?: boolean;
  /**
   * Read only state
   */
  readOnly?: boolean;
  /**
   * Size of the input
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Custom border color (only applies to default state, not focus/error)
   */
  borderColor?: string;
  /**
   * Remove border radius
   */
  hasNoRadius?: boolean;
  /**
   * Remove box shadow
   */
  hasNoBoxShadow?: boolean;
}

/**
 * CustomTextInput component - an enhanced text input with clear button, prefix/suffix, and character count
 */
export const CustomTextInput: React.FC<CustomTextInputProps> = ({
  label,
  error,
  helperText,
  type = 'text',
  fullWidth = false,
  value = '',
  onChange,
  placeholder,
  className = '',
  disabled = false,
  required = false,
  showCharCount = false,
  maxLength,
  clearable = false,
  prefix,
  suffix,
  onClear,
  onBlur,
  onFocus,
  onKeyDown,
  name,
  id,
  autoComplete,
  autoFocus = false,
  readOnly = false,
  size = 'medium',
  borderColor,
  hasNoRadius = false,
  hasNoBoxShadow = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  const hasError = !!error;
  const charCount = currentValue.length;
  const showClearButton = clearable && currentValue.length > 0 && !disabled && !readOnly;

  const handleChange = (newValue: string) => {
    setCurrentValue(newValue);
    onChange?.(newValue);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentValue('');
    onChange?.('');
    onClear?.();
    inputRef.current?.focus();
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  // Sync external value changes
  React.useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  return (
    <TextField
      className={clsx(
        styles.wrapper,
        { [styles.wrapperFullWidth]: fullWidth },
        className
      )}
      style={{
        ...(borderColor ? { '--custom-border-color': borderColor } as React.CSSProperties : {}),
      }}
      isDisabled={disabled}
      isRequired={required}
      isReadOnly={readOnly}
      value={currentValue}
      onChange={handleChange}
      name={name}
      id={id}
      autoComplete={autoComplete}
    >
      {label && (
        <Label className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </Label>
      )}
      
      <div
        className={clsx(
          styles.inputWrapper,
          styles[`inputWrapper${size.charAt(0).toUpperCase() + size.slice(1)}`],
          {
            [styles.inputWrapperFocused]: isFocused,
            [styles.inputWrapperError]: hasError,
            [styles.inputWrapperDisabled]: disabled,
            [styles.inputWrapperCustomBorder]: !!borderColor,
            [styles.inputWrapperNoRadius]: hasNoRadius,
            [styles.inputWrapperNoBoxShadow]: hasNoBoxShadow,
          }
        )}
      >
        {prefix && (
          <div className={styles.prefix}>
            {prefix}
          </div>
        )}
        
        <Input
          ref={inputRef}
          type={type}
          placeholder={placeholder}
          className={clsx(
            styles.input,
            styles[`input${size.charAt(0).toUpperCase() + size.slice(1)}`],
            {
              [styles.inputDisabled]: disabled,
              [styles.inputReadOnly]: readOnly,
            }
          )}
          maxLength={maxLength}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          autoFocus={autoFocus}
          aria-invalid={hasError}
          aria-describedby={error || helperText ? 'input-helper' : undefined}
        />
        
        {showClearButton && (
          <button
            type="button"
            onClick={handleClear}
            className={styles.clearButton}
            aria-label="Clear input"
          >
            ×
          </button>
        )}
        
        {suffix && !showClearButton && (
          <div className={styles.suffix}>
            {suffix}
          </div>
        )}
      </div>

      {((error || helperText) || showCharCount) && (
        <div
          id="input-helper"
          className={clsx(styles.helper, { [styles.helperError]: hasError })}
        >
          <span>{error || helperText}</span>
          {showCharCount && (
            <span className={clsx(styles.charCount, { [styles.charCountError]: maxLength && charCount > maxLength })}>
              {charCount}{maxLength ? `/${maxLength}` : ''}
            </span>
          )}
        </div>
      )}
    </TextField>
  );
};

CustomTextInput.displayName = 'ProdiktCustomTextInput';
